import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const PERSONAS = [
  {
    tag: "Primary",
    id: "independent-seniors",
    title: "Independent Seniors, 65+",
    body:
      "Older adults choosing to age in place safely, with light to moderate daily assistance, dignity, and routine intact.",
    img: "https://customer-assets.emergentagent.com/job_diaspora-care-hub/artifacts/26wxvgx8_WhatsApp%20Image%202026-07-01%20at%202.29.15%20PM.jpeg",
    imgAlt: "A group of senior friends playing chess and sharing conversation in a park",
    detail: {
      lead:
        "For seniors who want to stay in their own home — with just the right amount of support to make daily life easier, safer, and more joyful.",
      offerings: [
        "Warm daily companionship & wellness check-ins",
        "Gentle mobility, bathing & personal hygiene support",
        "Non-medical medication reminders and adherence",
        "Light exercise encouragement and daily activity accompaniment",
        "Home safety assessments to reduce fall risk",
        "Custom hours-based care subscriptions",
      ],
      cta: "Discuss a care plan",
    },
    accent: "linear-gradient(135deg, #FFE5D9 0%, #FBD9C4 100%)",
  },
  {
    tag: "Primary",
    id: "returning-residents",
    title: "Returning Residents",
    body:
      "Retirees relocating home to Jamaica, seeking a stress-free, white-glove transition with continuity of care.",
    img: "https://customer-assets.emergentagent.com/job_diaspora-care-hub/artifacts/17kdnw8c_WhatsApp%20Image%202026-07-01%20at%2012.21.40%20PM%20%281%29.jpeg",
    imgAlt: "Sangster International Airport in Montego Bay, Jamaica — welcoming returning residents home",
    detail: {
      lead:
        "For diaspora retirees ready to come home — we coordinate every stage from housing to healthcare so the transition feels effortless.",
      offerings: [
        "Pre-relocation lifestyle consultation and needs assessment",
        "Parish, neighborhood, and community safety guidance",
        "Verified real-estate agent, landlord & lawyer referrals",
        "Property purchase or rental coordination",
        "Pre-arrival move-in inspection, cleaning & pantry stocking",
        "First 30–90 days of settlement concierge support",
      ],
      cta: "Plan my relocation",
    },
    accent: "linear-gradient(135deg, #E8DCF3 0%, #C9B6E0 100%)",
  },
  {
    tag: "Secondary",
    id: "diaspora-adult-children",
    title: "Diaspora & Adult Children",
    body:
      "Long-distance family caregivers managing the safety and logistics of aging parents in Jamaica from abroad.",
    img: "https://customer-assets.emergentagent.com/job_diaspora-care-hub/artifacts/isjoacl4_WhatsApp%20Image%202026-07-01%20at%2012.21.40%20PM.jpeg",
    imgAlt: "Adult daughter on a video call with her senior parents at home, staying connected across the diaspora",
    detail: {
      lead:
        "For adult children abroad — a trusted set of hands on the ground, and clear, written updates so you always know how mom or dad is doing.",
      offerings: [
        "Weekly written wellness reports with photos",
        "Virtual property or home walk-throughs on request",
        "Medical appointment scheduling & transport coordination",
        "Pharmacy pickups and prescription management",
        "WhatsApp group communication with the care team",
        "Absentee property oversight & security check-ins",
      ],
      cta: "Set up family updates",
    },
    accent: "linear-gradient(135deg, #FBD9C4 0%, #E8DCF3 100%)",
  },
];

const PersonaCard = ({ p, onOpen }) => (
  <article
    data-testid={`persona-${p.id}`}
    className="group rounded-2xl overflow-hidden bg-white border border-[var(--gg-border)] hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
  >
    <div
      className="relative aspect-[4/3] flex items-center justify-center overflow-hidden"
      style={{ background: p.accent }}
    >
      <img
        src={p.img}
        alt={p.imgAlt || p.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
        loading="lazy"
      />
      <div className="absolute top-4 left-4 z-10">
        <span className="text-[10px] tracking-[0.2em] uppercase bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[var(--gg-primary)] font-semibold">
          {p.tag}
        </span>
      </div>
    </div>
    <div className="p-7">
      <h3 className="font-serif text-2xl text-[var(--gg-deep)] font-semibold">
        {p.title}
      </h3>
      <p className="text-sm text-[var(--gg-text)] leading-relaxed mt-3 font-medium">
        {p.body}
      </p>
      <button
        onClick={() => onOpen(p.id)}
        data-testid={`persona-learn-more-${p.id}`}
        className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--gg-primary)] hover:text-[var(--gg-primary-dark)] font-semibold group/btn"
      >
        Learn more
        <ArrowRight
          size={15}
          className="transition-transform group-hover/btn:translate-x-1"
        />
      </button>
    </div>
  </article>
);

const PersonaDialog = ({ p, isOpen, onOpenChange, onClose }) => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid={`persona-dialog-${p.id}`}
      className="max-w-lg bg-white"
    >
      <DialogHeader>
        <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--gg-primary)] font-semibold">
          {p.tag} · Who we serve
        </span>
        <DialogTitle className="font-serif text-3xl text-[var(--gg-deep)] mt-2">
          {p.title}
        </DialogTitle>
        <DialogDescription className="text-[var(--gg-text)] leading-relaxed pt-2">
          {p.detail.lead}
        </DialogDescription>
      </DialogHeader>
      <ul className="mt-4 space-y-2.5">
        {p.detail.offerings.map((it) => (
          <li key={it} className="flex items-start gap-3 text-sm text-[var(--gg-text)]">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gg-primary)] flex-shrink-0" />
            {it}
          </li>
        ))}
      </ul>
      <a
        href="#contact"
        onClick={onClose}
        data-testid={`persona-cta-${p.id}`}
        className="mt-6 inline-flex items-center justify-center gap-3 bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)] transition-colors rounded-full px-6 py-3 text-sm w-full sm:w-auto"
      >
        {p.detail.cta}
        <ArrowRight size={15} />
      </a>
    </DialogContent>
  </Dialog>
);

export default function PersonasSection() {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="for-whom"
      data-testid="personas-section"
      className="py-28 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow">Who we serve</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--gg-deep)] mt-5 leading-[1.05] font-semibold">
            Built for three quiet kinds of trust.
          </h2>
          <p className="mt-5 text-lg text-[var(--gg-text)] leading-relaxed max-w-2xl">
            Whether you&apos;re aging in place, coming home to Jamaica, or
            watching over a loved one from abroad — we&apos;ve designed a path
            for you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {PERSONAS.map((p) => (
            <PersonaCard key={p.id} p={p} onOpen={setOpen} />
          ))}
        </div>
      </div>

      {PERSONAS.map((p) => (
        <PersonaDialog
          key={p.id}
          p={p}
          isOpen={open === p.id}
          onOpenChange={(v) => setOpen(v ? p.id : null)}
          onClose={() => setOpen(null)}
        />
      ))}
    </section>
  );
}
